import type { Event } from 'models';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Button, Card, Icon, Space, message } from 'ui';
import { useEventRsvpLabels } from '../../../hooks';
import { INDIVIDUAL_INVITATION_LIMIT } from '../../../invitations-form-content';
import { EventInviteesModal } from '../../event-invitees-modal';
import { useEventInviteesCount } from '../../hooks';
import { EventAdmins } from './event-admins';
import { EventInvitees } from './event-invitees';
import { InviteMorePeopleModal } from './invite-more-people-modal';
import { SendInvitationsModal } from './send-invitations-modal';

type P = {
  event: Event;
};

const EventDetailInvitees = ({ event }: P) => {
  const { goingCount, id } = event;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInviteMorePeopleModalOpen, setIsInviteMorePeopleModalOpen] = useState(false);
  const [isSendInvitationsModalOpen, setIsSendInvitationsModalOpen] = useState(false);
  const { getAcceptedSectionTitle } = useEventRsvpLabels();
  const { canInviteUserToEvent, isEventsAdmin } = usePermissions();
  const eventInviteesCount = useEventInviteesCount(event);
  const { t } = useTranslation();

  const handleInviteMorePeople = useCallback(() => {
    if (!isEventsAdmin({})) {
      if (eventInviteesCount >= INDIVIDUAL_INVITATION_LIMIT) {
        message.error(
          t("You've reached the {{count}}-invitation limit for your role. Remove someone to add another.", {
            count: INDIVIDUAL_INVITATION_LIMIT,
          }),
        );
        return;
      }
      setIsSendInvitationsModalOpen(true);
      return;
    }
    setIsInviteMorePeopleModalOpen(true);
  }, [eventInviteesCount, isEventsAdmin, t]);

  return (
    <>
      <Card
        extra={
          <Button
            onClick={() => {
              setIsModalOpen(true);
            }}
            type="link-primary"
          >
            See all
          </Button>
        }
        title={
          <Card.Title icon={<Icon icon="check-circle-line" />}>
            {getAcceptedSectionTitle({ count: goingCount })}
          </Card.Title>
        }
      >
        <Space block orientation="vertical" size="middle">
          <EventAdmins eventId={id} />
          <EventInvitees event={event} />
          {canInviteUserToEvent({ event }) ? (
            <Button block icon={<Icon icon="user-add-line" />} onClick={handleInviteMorePeople} type="secondary">
              Invite more people
            </Button>
          ) : null}
        </Space>
      </Card>

      <EventInviteesModal
        event={event}
        isOpened={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onOpenInviteModal={() => {
          setIsSendInvitationsModalOpen(true);
        }}
      />
      <InviteMorePeopleModal
        event={event}
        isOpened={isInviteMorePeopleModalOpen}
        onClose={() => {
          setIsInviteMorePeopleModalOpen(false);
        }}
      />
      <SendInvitationsModal
        eventId={id}
        eventInviteesCount={eventInviteesCount}
        isOpened={isSendInvitationsModalOpen}
        onClose={() => {
          setIsSendInvitationsModalOpen(false);
        }}
      />
    </>
  );
};

export default EventDetailInvitees;
